'use client';

import { useTranslations } from 'next-intl';
import type { NearbyLocationsResolved } from './types';
import { LinkList } from '@/components/LinkList';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { useSubstitute } from '@/lib/render/RenderContextProvider';

export function NearbyLocationsComponent({
  resolved,
}: {
  resolved: NearbyLocationsResolved;
}) {
  const t = useTranslations('nearbyLocations');
  const sub = useSubstitute();
  const items = resolved.items ?? [];

  if (items.length === 0) {
    return null;
  }

  // The built-in label, used for every entry the backend sent no `label` for:
  // city entries read "Nachhilfe in {name}" and jobs entries "Nachhilfe geben
  // in {name}" (i18n); location entries keep the bare Standort name.
  const builtInLabelOf = (name: string) => {
    switch (resolved.resultType) {
      case 'city':
        return t('cityLabel', { name });
      case 'jobs-nearby-cities':
        return t('jobsLabel', { name });
      default:
        return name;
    }
  };

  // An editor's label arrives with its per-row tokens already resolved; `sub`
  // finishes the page-level ones (`{domain.*}`) the backend left alone.
  const labelOf = (item: (typeof items)[number]) =>
    item.label ? sub(item.label) : builtInLabelOf(item.name);
  const tracking = resolved.tracking?.length ? resolved.tracking : undefined;

  return (
    <section className="pt-4">
      <LinkList
        links={items.map((item) => {
          const label = labelOf(item);
          return {
            label,
            href: item.href,
            key: item.slug,
            onClick: tracking
              ? () => pushCmsEventTracking(tracking, { label })
              : undefined,
          };
        })}
      />
    </section>
  );
}
